import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ApiService from '../services/api';
import { trackPageView, trackPurchase as gtmTrackPurchase } from '../utils/gtm';
import * as Analytics from '../utils/analytics';
import './PaymentSuccess.css';

function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [reportId, setReportId] = useState(null);
  const [error, setError] = useState(null);
  const hasExecuted = useRef(false);

  useEffect(() => {
    // Track page view
    trackPageView('/payment/success', 'Payment Success - OSIN7');
    
    const confirmAndExecute = async () => {
      // Prevent multiple executions (React StrictMode, re-renders, etc.)
      if (hasExecuted.current) {
        console.log('⏭️ Skipping duplicate execution');
        return;
      }
      hasExecuted.current = true;

      try {
        // Get session_id from URL query params (added by Stripe redirect)
        const sessionId = searchParams.get('session_id');
        
        // Get report_id from sessionStorage (set before redirect to Stripe)
        const pendingReportId = sessionStorage.getItem('pending_report_id');
        const upgradeReportId = sessionStorage.getItem('upgrade_report_id');
        const currentReportId = pendingReportId || upgradeReportId;

        console.log('🔍 Payment success - session_id:', sessionId);
        console.log('🔍 Payment success - report_id:', currentReportId);

        if (!sessionId && !currentReportId) {
          console.error('❌ No session_id or report_id found');
          setStatus('error');
          setError('Payment information not found. Please contact support.');
          return;
        }

        setReportId(currentReportId);

        // Step 1: Confirm payment with Stripe and add credits to Firestore
        console.log('💳 Confirming payment with Stripe...');
        const confirmResult = await ApiService.confirmPayment({
          session_id: sessionId,
          report_id: currentReportId,
        });

        console.log('✅ Payment confirmed:', confirmResult);

        if (confirmResult.status === 'success' || confirmResult.status === 'paid') {
          const finalReportId = confirmResult.report_id || currentReportId;
          
          // Track successful purchase (both GTM and Firebase Analytics)
          const tier = confirmResult.tier || sessionStorage.getItem('selected_tier') || 'tier1';
          const selectorType = confirmResult.selector_type || sessionStorage.getItem('selector_type') || 'email';
          const amount = confirmResult.amount || (tier === 'tier1' ? 100 : 500); // fallback amounts
          gtmTrackPurchase(finalReportId, tier, selectorType, amount);
          Analytics.trackPurchase(finalReportId, tier, selectorType, amount);
          
          // Step 2: Execute the search job
          console.log('🚀 Executing search job...');
          await ApiService.executeJob(finalReportId);
          console.log('✅ Job execution started!');

          // Clear sessionStorage
          sessionStorage.removeItem('pending_report_id');
          sessionStorage.removeItem('upgrade_report_id');

          // Step 3: Redirect to report page
          console.log('📄 Redirecting to report page...');
          setTimeout(() => {
            navigate(`/report/${finalReportId}`);
          }, 1500);
        } else {
          console.error('❌ Payment not completed:', confirmResult);
          setStatus('error');
          setError(`Payment status: ${confirmResult.payment_status || 'unknown'}`);
        }
      } catch (err) {
        console.error('❌ Error in payment confirmation flow:', err);
        setStatus('error');
        setError(err.message || 'Failed to process payment. Please contact support.');
      }
    };

    confirmAndExecute();
  }, [navigate, searchParams]);

  return (
    <div className="payment-page">
      <div className="payment-container">
        {status === 'processing' && (
          <div className="payment-processing">
            <div className="success-icon">
              <i className="bi-check-circle"></i>
            </div>
            <h1>Payment Successful! 🎉</h1>
            <p className="payment-message">
              Confirming your payment and starting your search...
            </p>
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
            <p className="payment-note">
              This usually takes just a few seconds
            </p>
            {reportId && (
              <p className="report-id-hint">
                Report ID: <code>{reportId}</code>
              </p>
            )}
          </div>
        )}
        
        {status === 'error' && (
          <div className="payment-error">
            <div className="error-icon">
              <i className="bi-exclamation-triangle"></i>
            </div>
            <h1>Something went wrong</h1>
            <p className="error-message">{error}</p>
            {reportId && (
              <p className="error-details">
                Report ID: <code>{reportId}</code>
                <br />
                Please contact support with this ID.
              </p>
            )}
            <button 
              className="back-home-button"
              onClick={() => navigate('/')}
            >
              Return to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentSuccess;

